// E37 PR10 — THE INBOX, as the browser may ask about it.
//
// Contract: e35-37-design.md §5 PR10 — GET /api/inbox answers every mention of
//   the session's directory handles across every org and KB the member
//   belongs to (fan-out by the STORED membership handle alone — L20), each
//   item carrying the coordinates (org, kb), the mention's own fields, an
//   `href` that opens the PUBLISHED dashboard at the owner's #/r/ or #/c/
//   hash, and its read state. POST /api/inbox/read and /api/inbox/unread move
//   the read state; read accepts either {items} or {before} (mark-all-before).
// Contract: decision 0020 — the wire carries handles, names, keys and stamps;
//   never a user id. This client adds none.
//
// Shaped like src/api/reports.ts: late-bound fetch, refusals relayed in the
// server's own words.

import { appFetch } from './transport';
export type MentionTier = 'canonical' | 'operational' | 'conversational';

/** The owner address the /mentions door resolved (L13) — `hash` is what a
 *  published dashboard opens on (#/c/… for a document, #/r/… for a record). */
export interface InboxOwner {
  kind: string;
  doc: string;
  block: string;
  id: string;
  hash: string;
}

/** WHY an item is in this inbox (E39 PR10 — the called-out envelope change).
 *  `mention` is somebody typing your name; `watch` is something that happened
 *  to a record you follow. Required on both kinds, with no compat shim: an
 *  optional discriminant is one every reader has to guess about forever. */
export type InboxItemKind = 'mention' | 'watch';

/** What every item carries whatever its kind. */
interface InboxItemBase {
  org: string;
  kb: string;
  /** the read-state coordinate — mentionItemKey (`m:…`) or watchItemKey
   *  (`w:…`), one prefix-disjoint vocabulary over one table */
  key: string;
  kind: InboxItemKind;
  tier: MentionTier;
  node: string;
  block?: string;
  uuid?: string;
  /** the item's own words — the line the list renders */
  context: string;
  /** when it happened (ISO) — ONE field for both kinds, so the order, the
   *  badge and mark-all-read need no branch */
  firstAt: string;
  owner: InboxOwner | null;
  /** the published dashboard at the owner's hash; absent when the KB never adopted */
  href: string | null;
  read: boolean;
  readAt: string | null;
}

/** One mention of the reader, addressed: GET /api/inbox `items[n]`. */
export interface InboxMentionItem extends InboxItemBase {
  kind: 'mention';
  handle: string;
  raw: string;
  source: string;
  row?: string;
  column?: string;
  section?: string;
}

/** One thing that happened to a record the reader FOLLOWS. `event` is the
 *  source's own word (append · update · archive · restore · migrate · comment
 *  · reaction); `actor` is portable text — a handle, never an id (0020). */
export interface InboxWatchItem extends InboxItemBase {
  kind: 'watch';
  event: string;
  actor: string;
}

export type InboxItem = InboxMentionItem | InboxWatchItem;

/** One org of the scope report: which handle joined (null = none stored —
 *  L20's "no handle set for this org") and each KB's own fan-out outcome. */
export interface InboxOrgReport {
  org: string;
  handle: string | null;
  kbs: Array<{ kb: string; state: 'ok' | 'failed' }>;
}

export interface InboxAnswer {
  items: InboxItem[];
  orgs: InboxOrgReport[];
  unread: number;
}

/** The coordinates a mark names — exactly the wire's spelling. */
export interface InboxItemRef {
  org: string;
  kb: string;
  key: string;
}

export interface InboxClient {
  list(): Promise<InboxAnswer>;
  /** idempotent: re-marking a read item moves nothing (the first stamp holds) */
  markRead(items: InboxItemRef[]): Promise<{ marked: number }>;
  markUnread(items: InboxItemRef[]): Promise<{ unmarked: number }>;
  /** mark-all-before: everything listed at or before the ISO stamp */
  markAllReadBefore(before: string): Promise<{ marked: number }>;
}

async function refusalOf(response: Response, what: string): Promise<Error> {
  const body = (await response.json().catch(() => ({}))) as {
    guard?: string;
    reason?: string;
    message?: string;
    error?: string;
  };
  const said = body.reason ?? body.message ?? body.error;
  return new Error(
    `${body.guard ? `${body.guard}: ` : ''}${said ?? `${what} answered ${response.status}`}`,
  );
}

const str = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

function asOwner(raw: unknown): InboxOwner | null {
  if (raw === null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  return {
    kind: String(o.kind ?? ''),
    doc: String(o.doc ?? ''),
    block: String(o.block ?? ''),
    id: String(o.id ?? ''),
    hash: String(o.hash ?? ''),
  };
}

function asItem(raw: Record<string, unknown>): InboxItem {
  const base = {
    org: String(raw.org ?? ''),
    kb: String(raw.kb ?? ''),
    key: String(raw.key ?? ''),
    tier: (raw.tier ?? 'canonical') as MentionTier,
    context: String(raw.context ?? ''),
    node: String(raw.node ?? ''),
    ...(str(raw.block) ? { block: String(raw.block) } : {}),
    ...(str(raw.uuid) ? { uuid: String(raw.uuid) } : {}),
    firstAt: String(raw.first_at ?? ''),
    owner: asOwner(raw.owner ?? null),
    href: str(raw.href),
    read: raw.read === true,
    readAt: str(raw.read_at),
  };
  // THE DISCRIMINANT IS THE SERVER'S, and an unknown word reads as a mention:
  // this client predates nothing, but a future third kind must degrade to the
  // shape every field of this page already knows how to draw rather than
  // render blank.
  if (raw.kind === 'watch') {
    return {
      ...base,
      kind: 'watch',
      event: String(raw.event ?? ''),
      actor: String(raw.actor ?? ''),
    };
  }
  return {
    ...base,
    kind: 'mention',
    handle: String(raw.handle ?? ''),
    raw: String(raw.raw ?? ''),
    source: String(raw.source ?? ''),
    ...(str(raw.row) ? { row: String(raw.row) } : {}),
    ...(str(raw.column) ? { column: String(raw.column) } : {}),
    ...(str(raw.section) ? { section: String(raw.section) } : {}),
  };
}

export function createInboxClient(
  fetchImpl: typeof fetch = (input, init) => appFetch(input, init),
): InboxClient {
  async function post(path: string, body: unknown): Promise<Record<string, unknown>> {
    const response = await fetchImpl(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw await refusalOf(response, `POST ${path}`);
    return (await response.json()) as Record<string, unknown>;
  }

  return {
    async list() {
      const response = await fetchImpl('/api/inbox', { credentials: 'same-origin' });
      if (!response.ok) throw await refusalOf(response, 'GET /api/inbox');
      const body = (await response.json()) as {
        items?: Array<Record<string, unknown>>;
        orgs?: Array<Record<string, unknown>>;
        unread?: number;
      };
      const items = (body.items ?? []).map(asItem);
      return {
        items,
        orgs: (body.orgs ?? []).map((o) => ({
          org: String(o.org ?? ''),
          handle: str(o.handle),
          kbs: Array.isArray(o.kbs)
            ? (o.kbs as Array<Record<string, unknown>>).map((k) => ({
                kb: String(k.kb ?? ''),
                state: (k.state === 'failed' ? 'failed' : 'ok') as 'ok' | 'failed',
              }))
            : [],
        })),
        unread: typeof body.unread === 'number' ? body.unread : items.filter((i) => !i.read).length,
      };
    },

    async markRead(items) {
      const body = await post('/api/inbox/read', { items });
      return { marked: Number(body.marked ?? 0) };
    },

    async markUnread(items) {
      const body = await post('/api/inbox/unread', { items });
      return { unmarked: Number(body.unmarked ?? 0) };
    },

    async markAllReadBefore(before) {
      const body = await post('/api/inbox/read', { before });
      return { marked: Number(body.marked ?? 0) };
    },
  };
}
